import { useEffect, useId, useRef, useState } from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { Icon } from './Icon'
import { Popover } from './Popover'
import { Select, type SelectOption } from './Select'
import { cx } from '@/lib/cx'
import {
  formatDateInput,
  formatMonthYear,
  isDateInRange,
  MONTHS,
  parseCalendarDate,
  parseDateInput,
  todayIso,
  toIso,
  WEEKDAY_INITIALS,
} from '@/lib/dates'

/** How far a year list reaches when the field itself sets no bound. Back far
 *  enough for any living patient, forward far enough to book next year. */
const YEARS_BACK = 120
const YEARS_FORWARD = 10

interface InputProps {
  /** ISO `YYYY-MM-DD`, or '' for empty. The wire format never changes. */
  value: string
  onChange: (iso: string) => void
  id?: string
  label?: string
  describedBy?: string | undefined
  invalid?: boolean
  disabled?: boolean
  min?: string | undefined
  max?: string | undefined
  /** Off inside a table cell, where there is no room for a second control and
   *  typing is what the doctor is doing anyway. */
  withCalendar?: boolean
  className?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  /** Raised when a typed date is real but outside `min`/`max`, so the field can
   *  say why it refused instead of appearing to swallow the entry. */
  onRangeError?: (message: string | null) => void
}

/** Why a date was refused, in the clinic's words rather than the bounds'. */
function rangeMessage(min: string | undefined, max: string | undefined): string {
  const from = min ? formatDateInput(min) : ''
  const to = max ? formatDateInput(max) : ''
  if (min && max) return `Pick a date between ${from} and ${to}.`
  if (max) {
    return max === todayIso()
      ? 'A date in the future is not allowed.'
      : `Pick a date on or before ${to}.`
  }
  return `Pick a date on or after ${from}.`
}

/**
 * A date field that reads dd/mm/yyyy everywhere.
 *
 * A native `<input type="date">` renders its mask in the *browser's* locale — a
 * laptop set to en-US shows mm/dd/yyyy and nothing in HTML, CSS or JS can change
 * it. In a clinic that writes 25/09 that is not cosmetic: it is a date read
 * wrong. So the text is ours, and only ISO leaves the component.
 *
 * `min`/`max` bind the typed entry as well as the calendar. They used to grey
 * out days in the grid and nothing else, so a future date of birth went in by
 * hand and was only refused by the server on save.
 */
export function DateInput({
  value,
  onChange,
  id,
  label,
  describedBy,
  invalid = false,
  disabled = false,
  min,
  max,
  withCalendar = true,
  className,
  onKeyDown,
  onRangeError,
}: InputProps) {
  const [text, setText] = useState(() => formatDateInput(value))
  const [refused, setRefused] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLInputElement>(null)

  // The value can change under us — a draft restoring, a form resetting.
  useEffect(() => setText(formatDateInput(value)), [value])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Element
      if (wrap.current?.contains(target) || target.closest?.('[data-popover]')) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  function refuse(message: string | null) {
    setRefused(message)
    onRangeError?.(message)
  }

  /** Slashes appear as the digits do, so nobody has to type punctuation. A
   *  month typed by name is left alone — "25 sep 2026" is not a masked number. */
  function onType(raw: string) {
    const named = /[A-Za-z]/.test(raw)
    const masked = named
      ? raw.slice(0, 20)
      : [
          raw.replace(/\D/g, '').slice(0, 2),
          raw.replace(/\D/g, '').slice(2, 4),
          raw.replace(/\D/g, '').slice(4, 8),
        ]
          .filter(Boolean)
          .join('/')
    setText(masked)
    if (refused) refuse(null)

    const iso = parseDateInput(masked)
    // Out of range mid-type is not an error yet: they may still be typing the
    // year that brings it back in. Blur is where it has to be settled.
    if (iso && isDateInRange(iso, min, max)) onChange(iso)
    else if (masked.trim() === '') onChange('')
  }

  /** A half-typed date is not a date: on blur it is either valid or cleared. */
  function onBlur() {
    const iso = parseDateInput(text)
    if (iso && !isDateInRange(iso, min, max)) {
      refuse(rangeMessage(min, max))
      setText(formatDateInput(value))
      return
    }
    refuse(null)
    if (iso) {
      setText(formatDateInput(iso))
      onChange(iso)
    } else if (text.trim() === '') {
      onChange('')
    } else {
      setText(formatDateInput(value))
    }
  }

  return (
    <div className="relative" ref={wrap}>
      <input
        ref={field}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd/mm/yyyy"
        value={text}
        disabled={disabled}
        aria-label={label}
        aria-invalid={invalid || Boolean(refused) || undefined}
        aria-describedby={describedBy}
        onChange={(event) => onType(event.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={cx(
          className ?? FIELD_CONTROL,
          withCalendar && 'pr-touch',
          (invalid || refused) && FIELD_INVALID,
        )}
      />

      {/* A bare DateInput has no error slot of its own — a table cell has no
          room for one — so the reason is at least spoken. When a field is
          listening it renders the message itself, and announcing it here too
          would read it out twice. */}
      {!onRangeError && (
        <span role="status" aria-live="polite" className="sr-only">
          {refused ?? ''}
        </span>
      )}

      {withCalendar && (
        <button
          type="button"
          disabled={disabled}
          aria-label={open ? 'Close calendar' : 'Choose a date'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="absolute top-0 right-0 grid h-full w-touch place-items-center rounded-md text-fg-subtle transition-colors duration-150 ease-standard hover:text-fg disabled:text-fg-disabled"
        >
          <Icon name="calendar" size="1.125rem" />
        </button>
      )}

      <Popover anchor={field} open={open} align="start">
        <Calendar
          value={value}
          min={min}
          max={max}
          onPick={(iso) => {
            onChange(iso)
            setText(formatDateInput(iso))
            refuse(null)
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
        />
      </Popover>
    </div>
  )
}

interface FieldProps
  extends Omit<InputProps, 'id' | 'label' | 'describedBy' | 'invalid' | 'onRangeError'> {
  label: string
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
}

export function DateField({ label, required = false, error, hint, ...rest }: FieldProps) {
  const [refused, setRefused] = useState<string | null>(null)

  // The form's own error wins: it is about the saved value, not the keystroke.
  return (
    <Field label={label} required={required} error={error ?? refused ?? undefined} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <DateInput
          id={id}
          describedBy={describedBy}
          invalid={invalid}
          onRangeError={setRefused}
          {...rest}
        />
      )}
    </Field>
  )
}

function leadingBlanks(first: Date): number {
  /** Monday-first offset for the 1st of the month. */
  return (first.getDay() + 6) % 7
}

/** The same day in another month, or that month's last day — stepping from the
 *  31st to February must not land in March. */
function atMonth(base: Date, year: number, month: number): Date {
  const days = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(base.getDate(), days))
}

function Calendar({
  value,
  min,
  max,
  onPick,
  onClose,
}: {
  value: string
  min?: string | undefined
  max?: string | undefined
  onPick: (iso: string) => void
  onClose: () => void
}) {
  const today = todayIso()
  const names = useId()
  const clamp = (date: Date) => {
    const iso = toIso(date)
    if (min && iso < min) return parseCalendarDate(min)
    if (max && iso > max) return parseCalendarDate(max)
    return date
  }

  const [cursor, setCursor] = useState(() => clamp(parseCalendarDate(value || today)))
  const grid = useRef<HTMLDivElement>(null)
  // The grid takes focus when the calendar opens and when the arrows move it,
  // but not when a month or year is chosen — focus has to stay in the header
  // long enough to choose the other one.
  const focusGrid = useRef(true)

  useEffect(() => {
    if (!focusGrid.current) return
    grid.current?.querySelector<HTMLButtonElement>('[data-cursor="true"]')?.focus()
  }, [cursor])

  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const first = new Date(year, month, 1)
  const days = new Date(year, month + 1, 0).getDate()
  const blanks = leadingBlanks(first)

  const lower = min ? parseCalendarDate(min) : null
  const upper = max ? parseCalendarDate(max) : null

  const firstYear = lower ? lower.getFullYear() : new Date().getFullYear() - YEARS_BACK
  const lastYear = upper ? upper.getFullYear() : new Date().getFullYear() + YEARS_FORWARD
  const yearOptions: SelectOption<string>[] = Array.from(
    { length: Math.max(1, lastYear - firstYear + 1) },
    (_, i) => ({ value: String(firstYear + i), label: String(firstYear + i) }),
  )

  // Only the months the bounds actually leave open in the chosen year, so
  // picking one never silently snaps to a different month.
  const firstMonth = lower && year === lower.getFullYear() ? lower.getMonth() : 0
  const lastMonth = upper && year === upper.getFullYear() ? upper.getMonth() : 11
  const monthOptions: SelectOption<string>[] = MONTHS.slice(firstMonth, lastMonth + 1).map(
    (label, i) => ({ value: String(firstMonth + i), label }),
  )

  const prevDisabled = Boolean(min && toIso(new Date(year, month, 0)) < min)
  const nextDisabled = Boolean(max && toIso(new Date(year, month + 1, 1)) > max)

  function step(months: number) {
    focusGrid.current = true
    setCursor((c) => clamp(atMonth(c, c.getFullYear(), c.getMonth() + months)))
  }

  function move(deltaDays: number, deltaMonths = 0) {
    focusGrid.current = true
    setCursor((c) =>
      clamp(new Date(c.getFullYear(), c.getMonth() + deltaMonths, c.getDate() + deltaDays)),
    )
  }

  function onGridKeyDown(event: React.KeyboardEvent) {
    const weekday = (cursor.getDay() + 6) % 7
    const moves: Record<string, () => void> = {
      ArrowLeft: () => move(-1),
      ArrowRight: () => move(1),
      ArrowUp: () => move(-7),
      ArrowDown: () => move(7),
      PageUp: () => move(0, -1),
      PageDown: () => move(0, 1),
      Home: () => move(-weekday),
      End: () => move(6 - weekday),
    }
    const handler = moves[event.key]
    if (!handler) return
    event.preventDefault()
    handler()
  }

  function onDialogKeyDown(event: React.KeyboardEvent) {
    if (event.key !== 'Escape') return
    // An open month or year list owns its own Escape; closing the whole
    // calendar because a list was cancelled loses the date being chosen.
    if ((event.target as Element).closest?.('[role="combobox"][aria-expanded="true"]')) return
    event.preventDefault()
    onClose()
  }

  const navButton =
    'grid size-touch flex-none place-items-center rounded-md text-fg-muted hover:not-disabled:bg-hover hover:not-disabled:text-fg disabled:text-fg-disabled'

  return (
    <div
      role="dialog"
      aria-label="Choose a date"
      onKeyDown={onDialogKeyDown}
      className="w-80 rounded-lg border border-line bg-raised p-3 shadow-lg"
    >
      <span id={`${names}-month`} className="sr-only">
        Month
      </span>
      <span id={`${names}-year`} className="sr-only">
        Year
      </span>

      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous month"
          disabled={prevDisabled}
          onClick={() => step(-1)}
          className={navButton}
        >
          <Icon name="chevronLeft" size="1.125rem" />
        </button>

        <div className="min-w-0 flex-1">
          <Select
            labelledBy={`${names}-month`}
            options={monthOptions}
            value={String(month)}
            onChange={(next) => {
              focusGrid.current = false
              setCursor((c) => clamp(atMonth(c, c.getFullYear(), Number(next))))
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Select
            labelledBy={`${names}-year`}
            options={yearOptions}
            value={String(year)}
            onChange={(next) => {
              focusGrid.current = false
              setCursor((c) => clamp(atMonth(c, Number(next), c.getMonth())))
            }}
          />
        </div>

        <button
          type="button"
          aria-label="Next month"
          disabled={nextDisabled}
          onClick={() => step(1)}
          className={navButton}
        >
          <Icon name="chevronRight" size="1.125rem" />
        </button>
      </div>

      {/* The two selects say the month and year between them; a screen reader
          still wants the change announced as one phrase. */}
      <span aria-live="polite" className="sr-only">
        {formatMonthYear(cursor)}
      </span>

      <div className="grid grid-cols-7 gap-0.5" aria-hidden="true">
        {WEEKDAY_INITIALS.map((day) => (
          <span key={day} className="py-1 text-center text-2xs font-semibold text-fg-subtle">
            {day.slice(0, 1)}
          </span>
        ))}
      </div>

      <div ref={grid} onKeyDown={onGridKeyDown} className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: blanks }, (_, i) => (
          <span key={`blank-${i}`} />
        ))}
        {Array.from({ length: days }, (_, i) => {
          const date = new Date(year, month, i + 1)
          const iso = toIso(date)
          const isCursor = date.getDate() === cursor.getDate()
          const isSelected = iso === value
          const isToday = iso === today
          const blocked = !isDateInRange(iso, min, max)
          return (
            <button
              key={iso}
              type="button"
              data-cursor={isCursor}
              tabIndex={isCursor ? 0 : -1}
              disabled={blocked}
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={isSelected}
              onClick={() => onPick(iso)}
              className={cx(
                'grid h-9 place-items-center rounded-md text-sm tabular-nums transition-colors duration-150 ease-standard',
                isSelected
                  ? 'bg-accent font-semibold text-on-accent'
                  : blocked
                    ? 'cursor-not-allowed text-fg-disabled'
                    : 'text-fg hover:bg-hover',
                // Today keeps a ring rather than a fill, so it never competes
                // with the day that is actually chosen.
                isToday && !isSelected && 'ring-1 ring-accent-line',
              )}
            >
              {i + 1}
            </button>
          )
        })}
      </div>
    </div>
  )
}
